import React, { useState, useEffect } from "react";
import { DollarLineIcon, ListIcon } from "../../icons";
import { useNavigate } from "react-router-dom";
import { UserGroupIcon, CheckCircleIcon } from "@heroicons/react/24/outline";

export default function LeadMetrics() {
  const [metrics, setMetrics] = useState({
    total_leads: 0,
    converted_leads: 0,
    total_opportunity_value: "0",
    recent_quotations: 0,
  });
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMetrics = async () => {
      const token = sessionStorage.getItem("access_token");
      if (!token) {
        setError("Authorization required. Please log in.");
        navigate("/signin");
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/dashboard-summary`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch dashboard summary");
        }

        const result = await response.json();
        if (result.status === 200) {
          setMetrics({
            total_leads: result.data.total_leads,
            converted_leads: result.data.converted_leads,
            total_opportunity_value: result.data.total_opportunity_value,
            recent_quotations: result.data.recent_quotations,
          });
        } else {
          setError("Failed to load metrics");
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : "An unknown error occurred";
        setError(errorMessage);
        navigate("/signin");
      }
    };

    fetchMetrics();
  }, [navigate]);

  const handleViewMore = (path: string) => {
    navigate(path);
  };

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6">
      {/* <!-- Total Leads Metric --> */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
        <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-xl dark:bg-blue-900/50">
          <UserGroupIcon className="text-blue-600 size-6 dark:text-blue-300" />
        </div>
        <div className="flex items-end justify-between mt-5">
          <div>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Total Number of Leads
            </span>
            <h4 className="mt-2 font-bold text-gray-800 text-title-sm dark:text-white/90">
              {metrics.total_leads.toLocaleString()}
            </h4>
          </div>
          <div className="flex flex-col items-end gap-2">
            <button
              onClick={() => handleViewMore("/lead-list")}
              className="px-1 py-0 text-sm font-medium text-gray-900 bg-blue-500/20 backdrop-blur-sm rounded-md border border-blue-400/30 hover:bg-blue-600/30 transition-colors duration-200 dark:text-gray-100 dark:border-blue-300/20"
            >
              View More
            </button>
          </div>
        </div>
      </div>

      {/* <!-- Converted Leads Metric --> */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
        <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-xl dark:bg-green-900/50">
          <CheckCircleIcon className="text-green-600 size-6 dark:text-green-300" />
        </div>
        <div className="flex items-end justify-between mt-5">
          <div>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Total Number of Converted Leads
            </span>
            <h4 className="mt-2 font-bold text-gray-800 text-title-sm dark:text-white/90">
              {metrics.converted_leads.toLocaleString()}
            </h4>
          </div>
          <div className="flex flex-col items-end gap-2">
            <button
              onClick={() => handleViewMore("/lead-list")}
              className="px-1 py-0 text-sm font-medium text-gray-900 bg-blue-500/20 backdrop-blur-sm rounded-md border border-blue-400/30 hover:bg-blue-600/30 transition-colors duration-200 dark:text-gray-100 dark:border-blue-300/20"
            >
              View More
            </button>
          </div>
        </div>
      </div>

      {/* <!-- Total Opportunities Value Metric --> */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
        <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-xl dark:bg-yellow-900/50">
          <DollarLineIcon className="text-yellow-600 size-6 dark:text-yellow-300" />
        </div>
        <div className="flex items-end justify-between mt-5">
          <div>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Total Opportunities Value
            </span>
            <h4 className="mt-2 font-bold text-gray-800 text-title-sm dark:text-white/90">
              {Number(metrics.total_opportunity_value).toLocaleString()}
            </h4>
          </div>
          <div className="flex flex-col items-end gap-2">
            <button
              onClick={() => handleViewMore("/opportunity-list")}
              className="px-1 py-0 text-sm font-medium text-gray-900 bg-blue-500/20 backdrop-blur-sm rounded-md border border-blue-400/30 hover:bg-blue-600/30 transition-colors duration-200 dark:text-gray-100 dark:border-blue-300/20"
            >
              View More
            </button>
          </div>
        </div>
      </div>

      {/* <!-- Recent Quotations Metric --> */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
        <div className="flex items-center justify-center w-12 h-12 bg-purple-100 rounded-xl dark:bg-purple-900/50">
          <ListIcon className="text-purple-600 size-6 dark:text-purple-300" />
        </div>
        <div className="flex items-end justify-between mt-5">
          <div>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Recent Quotations
            </span>
            <h4 className="mt-2 font-bold text-gray-800 text-title-sm dark:text-white/90">
              {metrics.recent_quotations.toLocaleString()}
            </h4>
          </div>
          <div className="flex flex-col items-end gap-2">
            <button
              onClick={() => handleViewMore("/quotation-list")}
              className="px-1 py-0 text-sm font-medium text-gray-900 bg-blue-500/20 backdrop-blur-sm rounded-md border border-blue-400/30 hover:bg-blue-600/30 transition-colors duration-200 dark:text-gray-100 dark:border-blue-300/20"
            >
              View More
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}